import {ToastContainer} from "react-toastify"
import TitleComponent from "../../../components/TitleComponent/TitleComponent"
import QrCodeScanner from "../../../components/QrCodeScanner/QrCodeScanner"
import React, {useState} from "react"
import notifyToast from "../../../components/Toast/Toast"
import {useNavigate} from "react-router-dom"
import {saveViljuskarZapisiData} from "../../../api/viljuskari"
import {sredstvoInfo} from "../../../api/fmkNalozi"

interface MetaData {
    meta_key: string
    meta_value: string
}

const ViljuskariZapisiDetails: React.FC = () => {
    const [loading, setLoading] = useState(false)
    const [naziv, setNaziv] = useState("")
    const [sredstvoId, setSredstvoId] = useState("")
    const navigate = useNavigate()

    const [formData, setFormData] = useState({
        GORIVO: "",
        PLIN: "",
        VANJSKO_PRANJE_VILJUSKARA: false,
        ZAMJENA_ULJA: false,
        PRANJE_HLADNJAKA: false,
        sredstvo: "",
        SredstvoId: "",
        RadniSati: "",
    })

    const assetFound = !!naziv && !!sredstvoId
    const canSubmit = assetFound && String(formData.RadniSati ?? "").length > 0

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value, type, checked} = event.target
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === "checkbox" ? checked : value,
        }))

        if (name === "sredstvo" && value.length >= 3) {
            checkInventurniBroj(value)
        }
    }

    const handleQrScan = (data: string) => {
        setFormData((prevData) => ({...prevData, sredstvo: data}))
        checkInventurniBroj(data)
    }

    const checkInventurniBroj = async (invBroj: string) => {
        try {
            const data = await sredstvoInfo({invBroj})
            if (data) {
                setNaziv(data.SredstvoNaziv)
                setSredstvoId(data.SredstvoId)
                setFormData((prevData) => ({
                    ...prevData,
                    sredstvo: invBroj,
                    SredstvoId: data.SredstvoId,
                }))
            } else {
                setNaziv("")
                setSredstvoId("")
                setFormData((prevData) => ({
                    ...prevData,
                    sredstvo: invBroj,
                    SredstvoId: "",
                }))
            }
        } catch (error) {
            console.error("Error fetching data:", error)
            setNaziv("")
            setSredstvoId("")
            setFormData((prevData) => ({
                ...prevData,
                sredstvo: invBroj,
                SredstvoId: "",
            }))
        }
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setLoading(true)

        const transformedMeta: MetaData[] = Object.entries(formData).reduce((acc: MetaData[], [key, value]) => {
            if (key !== "SredstvoId" && key !== "RadniSati") {
                let metaValue
                if (typeof value === "boolean") {
                    metaValue = value ? "DA" : "NE"
                } else {
                    metaValue = value
                }
                acc.push({meta_key: key, meta_value: metaValue.toString()})
            }
            return acc
        }, [])

        const transformedData = {
            SredstvoId: formData.SredstvoId,
            RadniSati: formData.RadniSati,
            meta: transformedMeta,
        }

        try {
            const response = await saveViljuskarZapisiData(transformedData)

            if (response.error === 0) {
                notifyToast(true, response.message)
                setTimeout(() => navigate(-1), 2000)
            } else {
                notifyToast(false, response.message || "An unexpected error occurred.")
            }
        } catch (error: any) {
            console.error("Error: ", error)
            notifyToast(false, error.response?.data?.message || "An unexpected error occurred.")
        } finally {
            setLoading(false)
        }
    }

    const checkCardClass = (checked: boolean) =>
        `w-100 border rounded p-4 d-flex align-items-center justify-content-between ${checked ? "border-success bg-light" : "border-gray-300"}`

    const checkBadgeClass = (checked: boolean) => `badge ${checked ? "bg-success" : "bg-secondary"}`

    return (
        <div className="row">
            <ToastContainer />

            <div className="card mb-5 mb-xl-10 col-12">
                <div
                    className="card-header border-0 cursor-pointer"
                    role="button"
                    data-bs-target="#kt_account_profile_details"
                    aria-expanded="true"
                    aria-controls="kt_account_profile_details"
                >
                    <div className="card-title m-0 d-flex flex-column">
                        <TitleComponent name="Viljuškar - Zapis" />
                        <div className="text-muted fs-7 mt-1">
                            1) Skeniraj/upiši sredstvo • 2) Provjeri naziv • 3) Unesi radne sate • 4) Gorivo/Plin • 5) Označi radove • 6) Sačuvaj
                        </div>
                    </div>
                </div>

                <div id="kt_account_profile_details" className="collapse show">
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="card-body border-top p-9">
                            {/* HOW TO */}
                            <div className="alert alert-info mb-5">
                                <div className="fw-bold mb-1">Kako popuniti:</div>
                                <ol className="mb-0 ps-3">
                                    <li>Skeniraj QR kod (ili upiši inventurni broj).</li>
                                    <li>Provjeri da se pojavi naziv sredstva (zelena poruka).</li>
                                    <li>Unesi radne sate, zatim gorivo/plin i označi šta je urađeno.</li>
                                    <li>Klikni <b>Sačuvaj zapis</b>.</li>
                                </ol>
                            </div>

                            <div className="row g-4">
                                <div className="col-lg-6 col-sm-12">
                                    {/* STEP 1: Scan / manual */}
                                    <div className="card border-0 shadow-sm mb-4">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="fw-bold">1) Sredstvo</div>
                                                <span className="badge bg-light text-dark border">Korak 1</span>
                                            </div>

                                            <div className="row g-3 align-items-center">
                                                <div className="col-12 col-md-3">
                                                    <div className="d-grid">
                                                        <QrCodeScanner onScan={handleQrScan} />
                                                    </div>
                                                    <div className="text-muted fs-8 mt-2">Skeniraj QR</div>
                                                </div>

                                                <div className="col-12 col-md-9">
                                                    <input
                                                        type="text"
                                                        placeholder="Inventurni broj (npr. 100234)"
                                                        name="sredstvo"
                                                        value={formData.sredstvo}
                                                        onChange={handleInputChange}
                                                        className="form-control form-control-lg"
                                                    />
                                                    <div className="form-text">Sistem automatski traži sredstvo nakon unosa.</div>
                                                </div>
                                            </div>

                                            <div className="mt-3">
                                                {formData.sredstvo ? (
                                                    assetFound ? (
                                                        <div className="alert alert-success mb-0 py-3">
                                                            <div className="fw-bold">Sredstvo pronađeno</div>
                                                            <div className="fs-6">{naziv}</div>
                                                        </div>
                                                    ) : (
                                                        <div className="alert alert-warning mb-0 py-3">
                                                            <div className="fw-bold">Sredstvo nije pronađeno</div>
                                                            <div className="text-muted fs-7">Provjeri inventurni broj (cifra, razmak, slovo).</div>
                                                        </div>
                                                    )
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>

                                    {/* STEP 2: Radni sati */}
                                    <div className="card border-0 shadow-sm mb-4">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="fw-bold">2) Radni sati</div>
                                                <span className="badge bg-light text-dark border">Korak 2</span>
                                            </div>

                                            <label className="form-label fw-semibold">Upisati broj radnih sati</label>
                                            <input
                                                type="number"
                                                placeholder="Radni sati"
                                                name="RadniSati"
                                                value={formData.RadniSati}
                                                onChange={handleInputChange}
                                                className="form-control form-control-lg"
                                            />
                                            <div className="form-text">Upiši stanje brojača sa viljuškara.</div>
                                        </div>
                                    </div>

                                    {/* STEP 3: Gorivo / Plin */}
                                    <div className="card border-0 shadow-sm mb-4">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="fw-bold">3) Gorivo / Plin</div>
                                                <span className="badge bg-light text-dark border">Korak 3</span>
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-12">
                                                    <label className="form-label fw-semibold">GORIVO <span className="text-muted">(upiši samo nasutu količinu)</span></label>
                                                    <input
                                                        type="text"
                                                        placeholder="npr. 10L"
                                                        name="GORIVO"
                                                        value={formData.GORIVO}
                                                        onChange={handleInputChange}
                                                        className="form-control form-control-lg"
                                                    />
                                                </div>

                                                <div className="col-12">
                                                    <label className="form-label fw-semibold">PLIN <span className="text-muted">(upiši samo nasutu količinu)</span></label>
                                                    <input
                                                        type="text"
                                                        placeholder="npr. 1 boca"
                                                        name="PLIN"
                                                        value={formData.PLIN}
                                                        onChange={handleInputChange}
                                                        className="form-control form-control-lg"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* STEP 4: Radovi */}
                                    <div className="card border-0 shadow-sm mb-4">
                                        <div className="card-body">
                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                <div className="fw-bold">4) Šta je urađeno?</div>
                                                <span className="badge bg-light text-dark border">Korak 4</span>
                                            </div>

                                            <div className="row g-3">
                                                <div className="col-12">
                                                    <label className={checkCardClass(formData.VANJSKO_PRANJE_VILJUSKARA)} style={{cursor: "pointer"}}>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <input
                                                                type="checkbox"
                                                                name="VANJSKO_PRANJE_VILJUSKARA"
                                                                checked={formData.VANJSKO_PRANJE_VILJUSKARA}
                                                                onChange={handleInputChange}
                                                                className="form-check-input"
                                                                style={{transform: "scale(1.4)"}}
                                                            />
                                                            <div>
                                                                <div className="fw-bold">Vanjsko pranje viljuškara</div>
                                                                <div className="text-muted fs-7">Označi ako je urađeno pranje.</div>
                                                            </div>
                                                        </div>
                                                        <span className={checkBadgeClass(formData.VANJSKO_PRANJE_VILJUSKARA)}>
                              {formData.VANJSKO_PRANJE_VILJUSKARA ? "DA" : "NE"}
                            </span>
                                                    </label>
                                                </div>

                                                <div className="col-12">
                                                    <label className={checkCardClass(formData.PRANJE_HLADNJAKA)} style={{cursor: "pointer"}}>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <input
                                                                type="checkbox"
                                                                name="PRANJE_HLADNJAKA"
                                                                checked={formData.PRANJE_HLADNJAKA}
                                                                onChange={handleInputChange}
                                                                className="form-check-input"
                                                                style={{transform: "scale(1.4)"}}
                                                            />
                                                            <div>
                                                                <div className="fw-bold">Pranje hladnjaka</div>
                                                                <div className="text-muted fs-7">Označi ako je hladnjak opran.</div>
                                                            </div>
                                                        </div>
                                                        <span className={checkBadgeClass(formData.PRANJE_HLADNJAKA)}>
                              {formData.PRANJE_HLADNJAKA ? "DA" : "NE"}
                            </span>
                                                    </label>
                                                </div>

                                                <div className="col-12">
                                                    <label className={checkCardClass(formData.ZAMJENA_ULJA)} style={{cursor: "pointer"}}>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <input
                                                                type="checkbox"
                                                                name="ZAMJENA_ULJA"
                                                                checked={formData.ZAMJENA_ULJA}
                                                                onChange={handleInputChange}
                                                                className="form-check-input"
                                                                style={{transform: "scale(1.4)"}}
                                                            />
                                                            <div>
                                                                <div className="fw-bold">Zamjena ulja</div>
                                                                <div className="text-muted fs-7">Označi ako je urađena zamjena ulja.</div>
                                                            </div>
                                                        </div>
                                                        <span className={checkBadgeClass(formData.ZAMJENA_ULJA)}>
                              {formData.ZAMJENA_ULJA ? "DA" : "NE"}
                            </span>
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* SUBMIT */}
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body">
                                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                                                <div className="text-muted fs-7">
                                                    {!assetFound && formData.sredstvo ? (
                                                        <span>Ne možete sačuvati dok sistem ne pronađe sredstvo.</span>
                                                    ) : !formData.RadniSati ? (
                                                        <span>Unesite radne sate prije spremanja.</span>
                                                    ) : (
                                                        <span>Spremno za spremanje zapisa.</span>
                                                    )}
                                                </div>

                                                <button
                                                    type="submit"
                                                    className="btn btn-success btn-lg px-8"
                                                    disabled={loading || !canSubmit}
                                                >
                                                    {!loading && "Sačuvaj zapis"}
                                                    {loading && (
                                                        <span className="indicator-progress" style={{display: "block"}}>
                              Please wait...{" "}
                                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}

export default ViljuskariZapisiDetails
